import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import { fetchClientEconomics, type ClientEconomics, type ClientEconomicsRow } from "../services/workHoursService";
import { payPeriodPreset, presetRange, type RangePreset } from "../lib/heatmap";
import { describeRequestFailure } from "../lib/requestFailure";
import { buildThemeClasses } from "../theme";

// ---------------------------------------------------------------------------
// Tiempo, costo y ganancia por cliente (#1064). Solo dueños (la API responde
// 403 a cualquier otra persona). Dos gráficas y una tabla con los mismos datos:
//   · % del tiempo del equipo por cliente en el rango (+ «sin cliente»);
//   · costo, ingreso y ganancia en MXN por cliente.
// Costo = horas × costo/h del padrón (por moneda). Ingreso = lo que el cliente
// paga POR EL RANGO: los cortes de sus negocios con cobro recurrente (facturados
// o por facturar) y sus demás facturas; el histórico de negocios ganados va
// debajo, como referencia. Las quincenas se piden COMPLETAS (hasta su día de
// pago) para que salga lo que entrará en la quincena, no solo hasta hoy. Sin tipo
// de cambio, la ganancia sale «—» y el costo por moneda: nunca se inventa una
// conversión.
// ---------------------------------------------------------------------------

type Preset = Exclude<RangePreset, "custom">;
const PRESETS: Preset[] = ["qCurrent", "qPrevious", "30d", "90d"];

/**
 * Rango de un atajo. La quincena va completa (el tablero la corta en hoy): el
 * ingreso es el del corte con que termina; las horas de los días que faltan son cero.
 */
export const economicsRange = (preset: Preset, end?: string): { from: string; to: string } => {
  if (preset !== "qCurrent" && preset !== "qPrevious") return presetRange(preset, end);
  const period = payPeriodPreset(preset, end);
  return { from: period.from, to: period.to };
};

/** Colores validados en claro y oscuro (mismos tonos que Gastos). */
const COLORS = { share: "#7c3aed", other: "#9ca3af", cost: "#d97706", income: "#0d9488", profit: "#6366f1", loss: "#dc2626" };

const money = (value: number, currency: string, language: string) =>
  `${new Intl.NumberFormat(String(language).startsWith("en") ? "en-US" : "es-MX", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(value)} ${currency}`;

const costByCurrency = (row: Pick<ClientEconomicsRow, "cost">, language: string) =>
  Object.keys(row.cost.byCurrency)
    .sort()
    .map((c) => money(row.cost.byCurrency[c], c, language))
    .join(" · ");

export default function ClientEconomicsCard() {
  const { t, i18n } = useTranslation("common");
  const language = i18n?.language || "es";
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [preset, setPreset] = useState<Preset>("qCurrent");
  const [data, setData] = useState<ClientEconomics | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchClientEconomics(economicsRange(preset))
      .then((d) => {
        if (!alive) return;
        setData(d);
        setErrorKey(null);
      })
      .catch((err) => {
        if (!alive) return;
        setData(null);
        setErrorKey(describeRequestFailure(err).kind === "permission" ? "workHours.ownersOnly.title" : "workHours.economics.loadError");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [preset]);

  const shareData = useMemo(() => {
    if (!data) return [];
    const rows = data.clients.filter((c) => c.hours > 0).map((c) => ({ name: c.name, pct: c.sharePct, hours: c.hours, other: false }));
    if (data.unattributedHours > 0) rows.push({ name: t("workHours.economics.noClient"), pct: data.unattributedSharePct, hours: data.unattributedHours, other: true });
    return rows;
  }, [data, t]);

  const moneyData = useMemo(
    () =>
      (data?.clients ?? [])
        .filter((c) => c.hours > 0 || c.income.amountMxn > 0)
        .map((c) => ({ name: c.name, cost: c.cost.totalMxn, income: c.income.amountMxn, profit: c.profitMxn })),
    [data],
  );

  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const tooltipStyle = { fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" };
  const mxn = (v: number | null) => (v === null ? "—" : money(v, "MXN", language));
  /** «Cobrado … · Por cobrar … · Por facturar …»: solo las partes con importe. */
  const incomeParts = (income: ClientEconomicsRow["income"]) =>
    (
      [
        ["collected", income.collectedMxn],
        ["receivable", income.receivableMxn],
        ["toBill", income.toBillMxn],
      ] as const
    )
      .filter(([, v]) => (v ?? 0) > 0)
      .map(([k, v]) => t(`workHours.economics.incomeParts.${k}`, { amount: mxn(v ?? 0) }))
      .join(" · ");
  const kFormat = (v: unknown) => {
    const n = Number(v);
    return Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
  };
  const fxText = data
    ? Object.entries(data.fx)
        .map(([c, r]) => t("workHours.economics.fxUsed", { currency: c, rate: r.rate, date: r.date || "—", source: r.source || "—" }))
        .join(" · ")
    : "";

  return (
    <section data-testid="client-economics" className={`rounded-2xl border p-4 ${tc.cardBg} ${tc.border} space-y-4`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={`font-semibold ${tc.text}`}>{t("workHours.economics.title")}</h2>
          <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.economics.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-1" role="group">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              data-testid={`economics-preset-${p}`}
              onClick={() => setPreset(p)}
              className={`rounded-lg px-2.5 py-1 text-xs ${preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`}`}
            >
              {t(`workHours.economics.preset.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {errorKey && (
        <p data-testid="economics-error" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
          {t(errorKey)}
        </p>
      )}
      {loading && !data && !errorKey && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.economics.loading")}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="economics-totals">
            {[
              ["hours", `${data.teamHours} h`],
              ["cost", mxn(data.totals.costMxn)],
              ["income", mxn(data.totals.incomeMxn)],
              ["profit", mxn(data.totals.profitMxn)],
            ].map(([k, v]) => (
              <div key={k} className={`rounded-xl border p-3 ${tc.border}`}>
                <p className={`text-[11px] uppercase tracking-wide ${tc.textSecondary}`}>{t(`workHours.economics.kpi.${k}`)}</p>
                <p className={`text-lg font-semibold ${tc.text}`} data-testid={`economics-total-${k}`}>
                  {v}
                </p>
              </div>
            ))}
          </div>

          {data.fxMissing.length > 0 && (
            <p data-testid="economics-fx-missing" className="rounded-lg bg-amber-500/10 p-2 text-xs text-amber-600">
              {t("workHours.economics.fxMissing", { currencies: data.fxMissing.join(", ") })}
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>{t("workHours.economics.shareChart")}</p>
              {shareData.length === 0 ? (
                <p className={`py-6 text-center text-xs ${tc.textSecondary}`}>{t("workHours.economics.noHours")}</p>
              ) : (
                <div style={{ height: Math.max(160, shareData.length * 34) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={shareData} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={axis} tickFormatter={(v) => `${v}%`} />
                      <YAxis type="category" dataKey="name" tick={axis} width={110} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, item) => [`${v}% · ${item?.payload?.hours ?? 0} h`, t("workHours.economics.share")]} />
                      <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                        {shareData.map((r) => (
                          <Cell key={r.name} fill={r.other ? COLORS.other : COLORS.share} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div>
              <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>{t("workHours.economics.moneyChart")}</p>
              {moneyData.length === 0 ? (
                <p className={`py-6 text-center text-xs ${tc.textSecondary}`}>{t("workHours.economics.noHours")}</p>
              ) : (
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={moneyData} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                      <XAxis dataKey="name" tick={axis} interval={0} />
                      <YAxis tick={axis} tickFormatter={kFormat} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => (v === null || v === undefined ? "—" : money(Number(v), "MXN", language))} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="cost" name={t("workHours.economics.col.cost")} fill={COLORS.cost} />
                      <Bar dataKey="income" name={t("workHours.economics.col.income")} fill={COLORS.income} />
                      <Bar dataKey="profit" name={t("workHours.economics.col.profit")} fill={COLORS.profit}>
                        {moneyData.map((r) => (
                          <Cell key={r.name} fill={(r.profit ?? 0) < 0 ? COLORS.loss : COLORS.profit} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="economics-table">
              <thead>
                <tr className={`text-left text-xs ${tc.textSecondary}`}>
                  <th className="py-1">{t("workHours.clients.name")}</th>
                  <th className="py-1 text-right">{t("workHours.economics.col.hours")}</th>
                  <th className="py-1 text-right">{t("workHours.economics.col.share")}</th>
                  <th className="py-1 text-right">{t("workHours.economics.col.cost")}</th>
                  <th className="py-1 text-right">{t("workHours.economics.col.income")}</th>
                  <th className="py-1 text-right">{t("workHours.economics.col.profit")}</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <tr key={c.clientId} data-testid={`economics-row-${c.clientId}`} className={`border-t ${tc.border} ${tc.text}`}>
                    <td className="py-1.5 font-medium">
                      {c.name}
                      {!c.configured && <span className={`ml-1 text-[11px] ${tc.textSecondary}`}>· {t("workHours.economics.noRepos")}</span>}
                    </td>
                    <td className="py-1.5 text-right">{c.hours} h</td>
                    <td className="py-1.5 text-right" data-testid={`economics-share-${c.clientId}`}>
                      {c.sharePct}%
                    </td>
                    <td className="py-1.5 text-right text-xs" data-testid={`economics-cost-${c.clientId}`}>
                      {c.cost.totalMxn !== null ? mxn(c.cost.totalMxn) : costByCurrency(c, language) || "—"}
                      {c.cost.unpricedHours > 0 && (
                        <span className={`block text-[11px] ${tc.textSecondary}`}>{t("workHours.economics.unpriced", { hours: c.cost.unpricedHours })}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right text-xs" data-testid={`economics-income-${c.clientId}`}>
                      {mxn(c.income.amountMxn)}
                      {incomeParts(c.income) && (
                        <span className={`block text-[11px] ${tc.textSecondary}`} data-testid={`economics-income-parts-${c.clientId}`}>
                          {incomeParts(c.income)}
                        </span>
                      )}
                      {(c.income.unscheduledDeals ?? 0) > 0 && (
                        <span className="block text-[11px] text-amber-600" data-testid={`economics-income-unscheduled-${c.clientId}`}>
                          {t("workHours.economics.unscheduled", { count: c.income.unscheduledDeals })}
                        </span>
                      )}
                      {c.income.wonDeals > 0 && (
                        <span className={`block text-[11px] ${tc.textSecondary}`} data-testid={`economics-income-all-time-${c.clientId}`}>
                          {t("workHours.economics.allTime", { count: c.income.wonDeals, amount: mxn(c.income.allTimeMxn ?? 0) })}
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-1.5 text-right text-xs font-medium ${c.profitMxn !== null && c.profitMxn < 0 ? "text-red-500" : ""}`}
                      data-testid={`economics-profit-${c.clientId}`}
                    >
                      {mxn(c.profitMxn)}
                    </td>
                  </tr>
                ))}
                {data.unattributedHours > 0 && (
                  <tr className={`border-t ${tc.border} ${tc.textSecondary}`} data-testid="economics-row-unattributed">
                    <td className="py-1.5">{t("workHours.economics.noClient")}</td>
                    <td className="py-1.5 text-right">{data.unattributedHours} h</td>
                    <td className="py-1.5 text-right">{data.unattributedSharePct}%</td>
                    <td colSpan={3} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className={`text-[11px] ${tc.textSecondary}`} data-testid="economics-rules">
            {t("workHours.economics.rules", { from: data.from, to: data.to })}
            {fxText && ` ${fxText}.`}
          </p>
        </>
      )}
    </section>
  );
}
